import { ref, computed, onUnmounted } from 'vue';
import { useDeviceStore } from '~/store/device/deviceStore';

export interface UseIsMobileReturn {
  isMobileDevice: ReturnType<typeof ref<boolean>>;
  keyboardHeight: ReturnType<typeof ref<number>>;
  isKeyboardOpen: ReturnType<typeof ref<boolean>>;
}

const FALLBACK_POLL_INTERVAL = 100; // ms

export default function useIsMobile(): UseIsMobileReturn {
  const deviceStore = useDeviceStore();

  const mobile = /Mobi|Android|iPhone|iPad|iPod|Windows Phone|BlackBerry|Mobile/i.test(navigator.userAgent);

  // Core reactive state
  const isMobileDevice = ref(mobile);
  const keyboardHeight = ref(0);

  // Derived state: keyboard is open when height > 0
  const isKeyboardOpen = computed(() => keyboardHeight.value > 0);

  // Track if visualViewport API is available
  const hasVisualViewport = typeof visualViewport !== 'undefined';

  // Fallback polling state
  let fallbackInterval: ReturnType<typeof setInterval> | null = null;
  let lastInnerHeight = typeof window !== 'undefined' ? window.innerHeight : 0;

  /**
   * Updates keyboard height based on visualViewport or window.innerHeight delta
   */
  function updateKeyboardHeight() {
    if (!import.meta.client) return;

    if (hasVisualViewport && visualViewport) {
      const currentInnerHeight = window.innerHeight;
      const screenHeight = screen.height;

      // Check if in fullscreen/immersive mode where keyboard doesn't resize viewport
      const isFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement);

      if (isFullscreen) {
        const fullscreenKeyboardHeight = Math.max(
          0,
          screenHeight - visualViewport.height - visualViewport.offsetTop
        );
        keyboardHeight.value = fullscreenKeyboardHeight;
      } else {
        // Normal mode: keyboardHeight = windowInnerHeight - visualViewportHeight
        keyboardHeight.value = Math.max(0, currentInnerHeight - visualViewport.height);
      }
    } else {
      // Fallback: detect keyboard via window.innerHeight changes
      const currentInnerHeight = window.innerHeight;
      const heightDelta = lastInnerHeight - currentInnerHeight;

      // Keyboard opening: innerHeight decreases significantly (>= 150px threshold)
      if (heightDelta > 150 && currentInnerHeight < lastInnerHeight) {
        keyboardHeight.value = heightDelta;
      } else if (currentInnerHeight >= lastInnerHeight) {
        // Keyboard closing: innerHeight restored
        keyboardHeight.value = 0;
      }
      lastInnerHeight = currentInnerHeight;
    }
  }

  // Attach viewport listeners
  function attachListeners() {
    if (!import.meta.client) return;

    if (hasVisualViewport && visualViewport) {
      visualViewport.addEventListener('resize', updateKeyboardHeight);
      visualViewport.addEventListener('scroll', updateKeyboardHeight);
      // Initial calculation
      updateKeyboardHeight();
    } else {
      // Fallback: polling with debounce
      console.warn('[useIsMobile] visualViewport not available, using window.innerHeight fallback');
      fallbackInterval = setInterval(() => {
        updateKeyboardHeight();
      }, FALLBACK_POLL_INTERVAL);
    }
  }

  // Cleanup listeners
  function detachListeners() {
    if (!import.meta.client) return;

    if (hasVisualViewport && visualViewport) {
      visualViewport.removeEventListener('resize', updateKeyboardHeight);
      visualViewport.removeEventListener('scroll', updateKeyboardHeight);
    } else if (fallbackInterval !== null) {
      clearInterval(fallbackInterval);
      fallbackInterval = null;
    }
  }

  // Sync isMobileDevice to deviceStore (maintains existing behavior)
  if (mobile) {
    deviceStore.setIsMobile(true);
  } else {
    deviceStore.setIsMobile(false);
  }

  // Setup listeners on first call (client-side only)
  if (import.meta.client) {
    attachListeners();

    // Cleanup on component unmount (Vue will call onUnmounted)
    onUnmounted(() => {
      detachListeners();
    });
  }

  return {
    isMobileDevice,
    keyboardHeight,
    isKeyboardOpen,
  };
}
